import {
    ProviderDocumentTranscription, ProviderDocumentTranscriptionItem,
    ProviderDocumentUrl, ProviderPhone,
    SupportDocumentTranscription, SupportDocumentTranscriptionItem,
    SupportDocumentUrl, SupportPhone
} from "../../api";
import React, {useEffect, useRef, useState} from "react";

import styled from "@emotion/styled/macro";
import {Icon} from "@mdi/react";
import {Variables} from "../base";
import {mdiFastForward} from "@mdi/js";
import ContentLoader from "react-content-loader";

const LinePlaceholder = React.memo(() => {
    return (
        <ContentLoader speed={2} height={20} style={{ width: '100%' }} preserveAspectRatio="">
            <rect x="0" y="0" rx="0" ry="0" width="50%" height="20" />
        </ContentLoader>
    );
});

function getTimestampText(secondFloat: number|undefined) {
    if(secondFloat === undefined) {
        return "";
    }
    let seconds = Math.floor(secondFloat);
    const minutes = Math.floor(seconds / 60);
    seconds = seconds - (minutes * 60);
    return String(minutes).padStart(2, '0') + ":" + String(seconds).padStart(2, '0');
}

function getPhoneLengthText(resource: SupportPhone|ProviderPhone|undefined) {
    if(!resource) {
        return "";
    }
    const dateStart = new Date(resource.start);
    const dateEnd = new Date(resource.end);
    const dateDiff = dateEnd - dateStart;
    let seconds = Math.ceil(dateDiff / 1000);
    const minutes = Math.floor(seconds / 60);
    seconds = seconds - (minutes * 60);
    return minutes + " min, " + seconds + " sec";
}

const Transcription: React.FunctionComponent<{transcription: SupportDocumentTranscription, time: number, audioRef: React.MutableRefObject<any>}> = React.memo(( { transcription, time, audioRef }) => {

    const containerRef = useRef(null);
    const latestSpeakingRef = useRef(null);

    useEffect(() => {
        const element = latestSpeakingRef.current;
        const container = containerRef.current;

        if (element && container) {
            const isElementInView = () => {
                const elementTop = element.offsetTop;
                const elementBottom = elementTop + element.offsetHeight;
                const containerTop = container.scrollTop;
                const containerBottom = containerTop + container.offsetHeight;

                return elementBottom > containerTop && elementTop < containerBottom;
            };

            // Scroll the element into view if it's not visible
            if (!isElementInView()) {
                element.scrollIntoView({behavior: "smooth", block: "nearest", inline: "start"});
            }
        }
    }, [time]); // Empty dependency array means this runs once on mount



    function isCurrentTranscription(t: ProviderDocumentTranscriptionItem): boolean {
        const items = transcription.transcription;
        if(!items) {
            return false;
        }
        const channelItems = items.filter(i=>i.channel === t.channel);
        let lastItem = -1;
        let itemIndex = -2;

        for(let i=0; i < channelItems.length; i++) {
            if(t.start === channelItems[i].start) {
                itemIndex = i;
            }
            if(itemIndex > -2 && channelItems && channelItems[i] && channelItems[i].start && (channelItems[i].start > time)) {
                break;
            }
            lastItem = i;
        }
        return lastItem === itemIndex;
    }

    function getKeyForItem(t:ProviderDocumentTranscriptionItem|SupportDocumentTranscriptionItem|undefined) {
        if(t === undefined) {
            return '';
        }
        const s = (t.content ? t.content : '') + (t.start ? t.start : '');
        let hash = 5381;
        for (let i = 0; i < s.length; i++) {
            hash = (hash * 33) ^ s.charCodeAt(i);
        }
        // Convert the result to a positive value
        const hashText = hash >>> 0;
        return hashText;
    }

    function seekToStartTime(startTime:number|undefined) {
        if(startTime === undefined) {
            return;
        }
        const audioElem = audioRef.current;
        if (audioElem) {
            audioElem.currentTime = Math.floor(startTime);
            if(audioElem.paused) {
                audioElem.play();
            }
        }
    }

    const ActionIcon = styled(Icon)`
        height: 18px;
        width: 18px;
        fill: ${Variables.Colors.b300_l62};
    `;

    const builtContainer = <div style={{maxHeight: '500px', overflowY: 'auto'}} ref={containerRef}>
        {transcription?.transcription?.map(t => {
            const isCurrentPlayed = isCurrentTranscription(t);
            const isEnter = t.channel === 1;
            const fontColor = isEnter ? '#007b22' : '#946de6';
            return <div key={getKeyForItem(t)} ref={isCurrentPlayed ? latestSpeakingRef : null} style={{backgroundColor: '#fbfbfb', width: '100%', padding: '2px', border: '1px solid #ebebeb', borderRadius: '2px'}}>
                <div style={{width: '100%', display: 'flex', justifyContent: 'space-between'}}>
                    <div style={{fontWeight: isCurrentPlayed ? 'bold' : 'normal', color: fontColor}}>{isEnter ? 'Enter' : 'Recipient'}
                    </div>
                    <div>{isCurrentPlayed ? <></> : <span style={{verticalAlign: 'middle', paddingLeft: '5px', cursor: 'pointer'}}
                                                          onClick={() => seekToStartTime(t.start)}
                    ><ActionIcon path={mdiFastForward} ></ActionIcon></span>
                    }[{getTimestampText(t.start)}]</div>
                </div>
                <span style={{fontWeight: isCurrentPlayed ? 'bold' : 'normal', color: '#222'}}>
                    {t.content}
                </span>
            </div>
        })}
    </div>;

    return builtContainer;
});

export type DownloadDocumentFunctionType = (id: string) => Promise<SupportDocumentUrl|ProviderDocumentUrl|undefined>;
export type TranscribeDocumentFunctionType = (id: string) => Promise<SupportDocumentTranscription|ProviderDocumentTranscription|undefined>;


async function downloadPhoneTranscript(resource: SupportPhone|ProviderPhone|undefined, transcribeDocument: TranscribeDocumentFunctionType) {

        if(!resource) {
            return;
        }

        const supportDocumentTranscription = await transcribeDocument(resource?.document?.id || '');

        if(!supportDocumentTranscription) {
            return;
        }

        // Step 1 : Build up the text data
        let txtContent = supportDocumentTranscription.transcription?.map(t=> {
            return '[' + getTimestampText(t.start) + '] ' + (t.channel === 1 ? 'Enter' : 'Recipient') + ' : ' + t.content;
        }).join('\n');

        if(!txtContent) {
            txtContent = '';
        }

        // Step 2: Create a Blob with text data
        const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8;' });

        // Step 3: Create a URL for the Blob
        const url = URL.createObjectURL(blob);

        // Step 4: Create an anchor (<a>) element
        const link = document.createElement('a');
        link.href = url;
        link.download = 'transcript-' + (resource?.document?.id || '') + '.txt';

        // This makes the link not visible on the page
        link.style.visibility = 'hidden';

        // Append the link to the body
        document.body.appendChild(link);

        // Step 5: Trigger a click event on the anchor
        link.click();

        // Step 6: Cleanup
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
}

const PhoneDetails: React.FunctionComponent<{ phone: SupportPhone | ProviderPhone, downloadDocument: DownloadDocumentFunctionType, transcribeDocument: TranscribeDocumentFunctionType}> = React.memo(({ phone, downloadDocument, transcribeDocument }) => {
    const [currentTime, setCurrentTime] = useState(0);
    const [loading, setLoading] = useState(false);
    const [file, setFile] = useState<SupportDocumentUrl | ProviderDocumentUrl | undefined>();
    const [transcript, setTranscript] = useState<SupportDocumentTranscription | ProviderDocumentTranscription | undefined>();
    const documentId = phone.document?.id;
    const audioRef = useRef(null);

    useEffect(() => {
        (async () => {
            if (!documentId) {
                return;
            }

            setLoading(true);

            try {
                const res = await downloadDocument(documentId);
                const transcriptionRes = await transcribeDocument(documentId);
                setFile(res);
                setTranscript(transcriptionRes);
            } finally {
                setLoading(false);
            }

            // Event handler for the timeupdate event
            const handleTimeUpdate = () => {
                if (audioRef.current) {
                    setCurrentTime(audioRef.current.currentTime);
                }
            };

            // Adding the event listener
            const audioElem = audioRef.current;
            if (audioElem) {
                audioElem.addEventListener('timeupdate', handleTimeUpdate);
            }

            // Cleanup function to remove the event listener
            return () => {
                if (audioElem) {
                    audioElem.removeEventListener('timeupdate', handleTimeUpdate);
                }
            };
        })();
    }, [documentId]);


    return <span>
        {loading && <LinePlaceholder />}
        {file?.url && <audio controls ref={audioRef}>
            <source src={file?.url} type="audio/wav"/>
            <p>Your browser does not support the audio element.</p>
        </audio>}
        {transcript?.transcription && <Transcription transcription={transcript} time={currentTime} audioRef={audioRef}></Transcription>}
    </span>

});

export {
    PhoneDetails, downloadPhoneTranscript, getPhoneLengthText
};